<template>
  <div ref="dropZone" class="upload-images">
    <div
      v-show="dropHighlighted"
      id="file-upload-backdrop"
      class="file-upload-backdrop"
      :class="{_highlight: dropHighlighted}"
      @drop="dropHighlighted = false"
      @drop.stop.prevent="handleDrop"
    >
      <template v-if="showIcon">
        <SVGIcon
          id="upload-icon"
          class="file-upload-backdrop__upload-icon"
          :iconName="'cloud_upload'"
          :width="'100%'"
          :height="'200px'"
        />
        <div id="file-upload-backdrop-text" class="file-upload-backdrop__text">
          <span>Drag and drop images here</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SVGIcon from '@/components/SVGIcon.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useImporterStore } from '@/stores/useImporterStore.js';
import { useToast } from 'vue-toast-notification';

const $toast = useToast();

export default {
  name: 'GalleryDropZone',
  components: {
    SVGIcon,
  },
  props: {
    datasetID: {
      type: Number,
      default: null,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['upload-complete', 'upload-error'],
  setup() {
    const {
      importByType,
    } = useImporterStore();

    return {
      importByType,
    };
  },
  data() {
    return {
      files: null,
      dropHighlighted: false,
      dragCounter: 0,
      selectedImportScript: "",
      dataConnect: null,
      importScriptType: "images-folder",
      importScriptAnnsetID: 0,
      importerSource: "",
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    isDebugMode() {
      return this.$store.state.debugMode;
    },
    datasetName() {
      const dataset = this.$store.state?.datasets?.datasetList.find((d) => d.id === this.datasetID);
      return dataset.name;
    },
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    importParams() {
      const importParams = {
        import_type: 'images-folder',
        dataset_id: this.datasetID,
        dataset_name: this.datasetName,
        project_id: this.currentProject.id,
      };
      return importParams;
    },
  },
  created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);

    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'tasks/updateImportTaskStatus') {
        if (mutation.payload.data.status === 'complete') {
          this.$emit('upload-complete');
        }
      }
    });
  },
  mounted() {
    this.$refs.dropZone.parentElement.addEventListener('dragenter', this.handleDragEnter);
    this.$refs.dropZone.parentElement.addEventListener('dragover', this.handleDragOver);
    this.$refs.dropZone.parentElement.addEventListener('dragleave', this.handleDragLeave);
  },
  unmounted() {
    this.$refs.dropZone?.parentElement.removeEventListener('dragenter', this.handleDragEnter);
    this.$refs.dropZone?.parentElement.removeEventListener('dragover', this.handleDragOver);
    this.$refs.dropZone?.parentElement.removeEventListener('dragleave', this.handleDragLeave);
  },
  methods: {
    handleDragEnter(e) {
      e.preventDefault();
      this.dragCounter += 1;
      this.dropHighlighted = true;
    },
    handleDragOver(e) {
      e.preventDefault();
    },
    handleDragLeave() {
      this.dragCounter -= 1;
      if (this.dragCounter === 0) {
        this.dropHighlighted = false;
      }
    },
    handleDrop(e) {
      this.files = e.dataTransfer.files;
      this.upload(this.files);
    },
    async upload(files) {
      if (files == null || (files && files.length === 0)) return;

      const response = await this.dataConnect.checkQuota();
      if (response.error) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: response.error.message,
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        this.$emit('upload-complete');
        return;
      }
      const filesAvailable = response.result.quota.files - response.result.used.files;
      if (!(filesAvailable > 0)) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: "Files Upload Limit Exceeded",
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        this.$emit('upload-complete');
        return;
      }

      await this.s3MultipleFileUpload({ fileList: files, params: this.importParams });
    },

    async s3MultipleFileUpload({ fileList, params }) {
      const keys = [];

      // Trim paths
      for (const file of fileList) {
        let trimmedPath;
        const relativePath = file.relativePath || file.webkitRelativePath;
        if (relativePath) {
          // From folder select
          trimmedPath = relativePath.split('/').slice(1);
        } else {
          // From file select
          trimmedPath = [file.name];
        }

        keys.push(trimmedPath.join('/'));
      }

      // Get s3 presigned urls
      const presignedResponse = await this.s3CreatePresignedUrl({
        dataset_id: params.dataset_id,
        ignore_duplicate: true,
        keys,
      })
        .catch((error) => {
          throw error;
        });

      if (presignedResponse && presignedResponse.duplicate_count === keys.length) {
        return presignedResponse;
      }

      const newFileList = [];

      if (presignedResponse.presignedUrls && presignedResponse.presignedUrls.length === 0) {
        // quietly return, do nothing
        $toast.open({
          queue: true,
          duration: 5000,
          message: "No new images to upload",
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        this.$emit('upload-complete');
        return null;
      }
      if (presignedResponse) {
        const {
          importerSource, indexes, presignedUrls, tnPresignedUrls,
        } = presignedResponse;

        if (importerSource) {
          params.importer_source = importerSource;
        }

        presignedUrls.forEach((url, i) => {
          newFileList.push({
            url,
            thumbnail_url: tnPresignedUrls[i],
            file: fileList[indexes[i]],
          });
        });
      }
      this.processingFiles = false;

      const importResponse = await this.importByType({
        urls: newFileList,
        importParams: params,
      });

      this.$emit('upload-complete');

      return importResponse;
    },
    async getMaxImageSize() {
      return this.dataConnect.getAccountingMaxImageSize()
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
    async checkImageUploadLimit(imgCount) {
      return this.dataConnect.checkImageLimitForAccounting({
        image_count: imgCount,
      })
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
    async s3CreatePresignedUrl({ dataset_id, ignore_duplicate, keys }) {
      return this.dataConnect.s3CreatePresignedUrl({
        dataset_id,
        ignore_duplicate,
        keys,
      })
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
  },
};
</script>

<style lang="scss" scoped>

.upload-images {
  // display: flex;
  // flex-direction: column;
  // padding: 1rem;
  // overflow: auto;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.file-upload-backdrop {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 32px;
  align-items: center;
  justify-content: center;
  border: 2px dashed grey;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  pointer-events: auto;

  &__upload-icon {
    position: absolute;
    bottom: 50%;
    width: 48px;
    height: 48px;
    @include themify() {
      color: themed('color-primary');
    }
  }

  &__text {
    position: absolute;
    top: 50%;
    display: flex;
    flex-direction: column;

    & span {
      padding-top: 0.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      direction: rtl;
      color: #250E81;
      font-size: 16px;
      font-weight: bold;
    }

    & span + span {
      color: grey;
      font-size: 12px;
    }
  }

  &._highlight {
    padding: 27px;
    border: 3px dashed #250E81;
    background: #250e8125;
    animation-name: fill-upload-btn;
    animation-duration: 0.5s;

    & .file-upload-backdrop__upload-icon {
      width: 56px;
      height: 56px;
      animation-name: grow-upload-icon;
      animation-duration: 0.05s;
    }
    @keyframes grow-upload-icon {
      from {  width: 48px; height: 48px;}
      to {  width: 56px; height: 56px;}
    }

    @keyframes fill-upload-btn {
      from {  background: transparent;}
      to {  background: #250e8125;}
    }
  }
}

</style>
